// 設計系統色碼（對齊 Figma 設計稿）
export const colors = {
	textPrimary: "#101828",
	textSecondary: "#6a7282",
	textCard: "#364153",
	textMenu: "#364153", // alias for textCard, used in UserSidebar
	textLabel: "#4a5565",
	rmsNormal: "#00a63e",
	rmsDanger: "#e7000b",
	// 觀察／警戒的文字色（2026-10-08，設備卡 RMS 數字與 sparkline）：regionColors 的
	// 黃／琥珀是底色，直接當白底文字讀不到，這兩個取同色系加深（對白底約 5:1）。
	rmsObserve: "#7a7000",
	rmsAlert: "#9a6200",
	aiBg: "#fef2f2",
	aiBorder: "#ffc9c9",
	border: "#e5e7eb",
	bg: "#f9fafb",
	qrBg: "#f9fafb",
	qrText: "#4a5565",
	dropdownBorder: "#d1d5dc",
	// 圖表取得鍵盤焦點時的外框。沿用設備圖控制面板的選中藍，
	// 讓「這張圖正在接收方向鍵」在兩個頁面是同一個視覺訊號。
	focusRing: "#0ea5e9",
	cardShadow: "0 1px 2.6px rgba(0,0,0,0.1), 0 1px 1.75px -1px rgba(0,0,0,0.1)",
	// 切換鈕 active 藍。與趨勢圖頁 .timeButton（TrendChart.global.scss）一致，
	// 全頁 toggle 類按鈕的選中態統一用這個色。
	toggleActive: "#007bff",
	toggleBorder: "#ced4da",
	toggleText: "#495057",
	toggleHoverBg: "#e9ecef",
};

// 告警區間色。沿用 NotificationManagement 既有的分類配色，
// 讓標準設定頁的區間標籤與通知列表看到的是同一組顏色。
export const regionColors = {
	normal: "#00a63e",
	observe: "#F9F400",
	alert: "#F2B800",
	danger: "#DF0029",
	overheat: "#FF5722",
};

// 未知狀態標籤（通知／設備各區域的預設值 "unknown"）：不填色、虛線邊框。
// 文字與邊框沿用所在處的文字色，淺色頁面與深色鈴鐺面板都讀得清楚。2026-10-16 使用者定案。
export const unknownRegionStyle = {
	color: "inherit",
	backgroundColor: "transparent",
	border: "1px dashed currentColor",
};

// 三軸序列色。沿用既有 ThreeAxisLineChart 的紅／藍／綠，讓跨頁面的軸向配色一致。
export const axisColors = {
	x: "#dc3545",
	y: "#0d6efd",
	z: "#198754",
};

// 3D 場景上的浮層（玻璃風）。數值沿用 RealPlantView 既有浮層，
// 深色半透明底讓白字在任何場景亮度下都讀得到。
export const sceneOverlay = {
	bg: "rgba(10, 14, 22, 0.55)",
	border: "rgba(255, 255, 255, 0.18)",
	text: "#ffffff",
	textMuted: "rgba(255, 255, 255, 0.85)",
};

// 三相電力序列色（2026-09-24）。沿用設備圖能源區塊既有的綠／紅／藍相別配色
// （EquipmentDiagram 的 V1/V2/V3、C1/C2/C3），讓電力數值在兩個頁面是同一組顏色。
// 電壓與電流共用相別色、以線條實虛區分，避免六條線六個色變成色票展示。
export const phaseColors = {
	l1: "#22c55e",
	l2: "#ff6b6b",
	l3: "#4dabf7",
};

// 電力衍生量。功率用琥珀色（與相別色拉開），頻率用灰藍當背景參考線。
export const powerColors = {
	power: "#fbbf24",
	frequency: "#94a3b8",
};

// 震後檢查（earthquake_triage）判定色。same 刻意用中性灰藍而非綠色：
// 「與震前相同」只表示量到的值相近，不等於設備安全，不能用綠色暗示。
export const triageColors = {
	changed: "#e7000b",
	changedBg: "#fef2f2",
	stateChanged: "#d97706",
	stateChangedBg: "#fffbeb",
	pending: "#6a7282",
	pendingBg: "#f3f4f6",
	noData: "#9ca3af",
	noDataBg: "#f9fafb",
	same: "#2563eb",
	sameBg: "#eff6ff",
	simulated: "#9ca3af",
	simulatedBg: "#f9fafb",
	originLine: "#e7000b",
	trendLine: "#2563eb",
	selectedBg: "#eff6ff",
};

// 原廠性能曲線圖（2026-10）。靜壓用藍、軸功率沿用 powerColors.power 的琥珀色，
// 設計點用紅色標記；等級標籤只有「認證實測」用強調綠，其餘用中性灰。
export const fanCurveColors = {
	pressure: "#4dabf7",
	power: "#fbbf24",
	designPoint: "#ff6b6b",
	operatingPoint: "#22c55e",
	axis: "rgba(255,255,255,0.55)",
	grid: "rgba(255,255,255,0.08)",
	badgeAccentBg: "rgba(34,197,94,0.14)",
	badgeAccentBorder: "rgba(34,197,94,0.4)",
	badgeAccentText: "#22c55e",
	badgeNeutralBg: "rgba(255,255,255,0.06)",
	badgeNeutralBorder: "rgba(255,255,255,0.18)",
	badgeNeutralText: "rgba(255,255,255,0.75)",
};

// 虛擬風量計（2026-10）。兩條曲線用不同色相以便並排辨識：認證／型錄＝藍（同 fanCurveColors.pressure）、
// 選型計算＝紫；不唯一／超出範圍用警示橘，錯誤用紅。
export const flowMeterColors = {
	certified: "#4dabf7",
	selection: "#a78bfa",
	warn: "#f59e0b",
	warnBg: "rgba(245,158,11,0.12)",
	warnBorder: "rgba(245,158,11,0.4)",
	error: "#ff6b6b",
	axis: "rgba(255,255,255,0.55)",
	grid: "rgba(255,255,255,0.08)",
	cardBg: "rgba(255,255,255,0.04)",
	cardBorder: "rgba(255,255,255,0.08)",
	textMuted: "rgba(255,255,255,0.55)",
	textFaint: "rgba(255,255,255,0.4)",
	textBody: "rgba(255,255,255,0.85)",
};

// 實測功率律面板（2026-10）。實測散佈用琥珀（沿用 powerColors.power）、擬合線用藍、
// 教科書 n=3 參考線用紅色虛線；低速固定損耗段用灰；空心分箱用警示紅、正常分箱用藍。
// 警示框（不可報價 / 估算功率）用琥珀底，不用綠色以免被誤讀成「通過」。
export const powerLawColors = {
	measured: "#fbbf24",
	lowSpeed: "rgba(255,255,255,0.28)",
	fit: "#4dabf7",
	textbook: "#ff6b6b",
	axis: "rgba(255,255,255,0.55)",
	grid: "rgba(255,255,255,0.08)",
	binOk: "#4dabf7",
	binHollow: "#ff6b6b",
	binEdge: "rgba(255,255,255,0.25)",
	warnBg: "rgba(251,191,36,0.10)",
	warnBorder: "rgba(251,191,36,0.4)",
	warnText: "#fbbf24",
};

// 安全轉速窗（2026-10）。禁區用紅色半透明底；目前頻率在禁區外用綠、在禁區內用紅；
// 1X／葉片通過頻率的激發點用不同顏色的細線標在橫條上。
export const safeSpeedColors = {
	track: "rgba(255,255,255,0.06)",
	trackBorder: "rgba(255,255,255,0.12)",
	forbiddenFill: "rgba(255,107,107,0.35)",
	forbiddenBorder: "rgba(255,107,107,0.8)",
	tick: "rgba(255,255,255,0.45)",
	markerSafe: "#22c55e",
	markerDanger: "#ff6b6b",
	excitation1x: "#fbbf24",
	excitationBpf: "#4dabf7",
};

// 原始訂單參考（2026-10）。中性淡底，與平台不一致的欄位用 safeSpeedColors.excitation1x 同一個琥珀色提醒，
// 不用紅色：不一致不代表錯，只是需要人確認哪一邊對。
export const orderReferenceColors = {
	bg: "rgba(255,255,255,0.04)",
	border: "rgba(255,255,255,0.12)",
	text: "rgba(255,255,255,0.65)",
	conflict: "#fbbf24",
};

// 屋頂聲學地圖（2026-10）。高頻（軸承異音）由安靜的青綠漸變到警示紅，讓「哪台在叫」一眼可辨；
// 低頻（廠界噪音）用琥珀到橘，與高頻區隔。真實資料標章用強調綠、示意標章用中性灰——
// 與 fanCurveColors 的「認證／一般」配色語意一致。
export const acousticColors = {
	highQuiet: "#2dd4bf",
	highWarn: "#fbbf24",
	highAlarm: "#ef4444",
	lowQuiet: "#fde68a",
	lowLoud: "#f97316",
	boundary: "#e879f9",
	unknown: "#a78bfa",
	beam: "#ff6b6b",
	panelBg: "rgba(10, 14, 22, 0.82)",
	panelBorder: "rgba(255,255,255,0.14)",
	text: "#ffffff",
	textMuted: "rgba(255,255,255,0.65)",
	textFaint: "rgba(255,255,255,0.42)",
	realBadgeBg: "rgba(34,197,94,0.16)",
	realBadgeBorder: "rgba(34,197,94,0.5)",
	realBadgeText: "#4ade80",
	mockBadgeBg: "rgba(255,255,255,0.08)",
	mockBadgeBorder: "rgba(255,255,255,0.22)",
	mockBadgeText: "rgba(255,255,255,0.7)",
	spectrogramLow: "#0b1020",
	spectrogramHigh: "#ffe066",
	faultBand: "rgba(239,68,68,0.22)",
	// 麥克風硬體外觀：陣列麥克風為黑色圓餅＋頂面淺色光環（依使用者提供的實機照），
	// 單點麥克風（煙囪出口）用白色圓點＋青色光暈，與熱圖色階區隔
	micBody: "#111214",
	micRingIdle: "#d9dde3",
	singleMic: "#f8fafc",
	singleMicHalo: "#22d3ee",
};

// 聲學熱圖色階（2026-10）：仿聲學相機的 jet 色階（藍→青→綠→黃→紅），使用者以 ReSpeaker
// 聲學相機截圖指定此呈現方式。位置 0–1 對應 acoustic/heatmap.ts 的 HEAT_MIN_DB–HEAT_MAX_DB。
export const acousticHeatmapStops: Array<[number, string]> = [
	[0, "#30123b"],
	[0.15, "#3e5bd8"],
	[0.32, "#2fb5dd"],
	[0.5, "#4be08f"],
	[0.66, "#c8ef34"],
	[0.8, "#fbb938"],
	[0.92, "#e4460a"],
	[1, "#7a0403"],
];
export const acousticPeakMarker = "#2563eb";

// 屋頂噪音模型（2026-10-07）：管制線（雲裡的等值殼、面板圖例）、女兒牆讀數、量測標記。
// 管制線用桃紫（與廠界 boundary 同色系），和雲本身的 jet 色階區隔；超標紅、合格青綠。
export const acousticNoiseColors = {
	limitShell: "#ff3df2",
	over: "#ef4444",
	overText: "#fca5a5",
	pass: "#2dd4bf",
	passText: "#99f6e4",
	edgeIdle: "rgba(255,255,255,0.35)",
	meterBody: "#111827",
	meterAccent: "#facc15",
	meterPole: "#e5e7eb",
	calibWarnBg: "rgba(250,204,21,0.12)",
	calibWarnBorder: "rgba(250,204,21,0.45)",
	calibWarnText: "#fde68a",
	miniRoof: "rgba(255,255,255,0.06)",
	miniRoofBorder: "rgba(255,255,255,0.3)",
	miniSolid: "rgba(255,255,255,0.16)",
	miniFan: "#93c5fd",
};

// CFD 流場軌跡（FlowTrails）：截面「粒子通過數」讀值標籤的色碼。
// 粒子本身的顏色走 streamlineDisplay 色階（與右側色條同源），不在此重複。
export const flowTrailColors = {
	readoutText: "#bfeaff",
	readoutUnit: "#8fb8cc",
	readoutBg: "rgba(7, 22, 36, 0.72)",
	readoutBorder: "rgba(99, 202, 235, 0.35)",
	// 四要素統一呈現：壓力以大氣（0 Pa）為中心的發散色階——負壓（被吸）藍、正壓（被推）紅。
	// 中性點不用純白：場景是淺灰屋頂＋天空，白線會消失，改用中灰藍。五個等距色票（-1、-.5、0、+.5、+1）。
	pressureStops: ["#1d4ed8", "#38bdf8", "#8a97a8", "#f59e0b", "#dc2626"],
	// 截面圓盤外框（CFD 截面照軟體慣例畫成管內實心圓盤，依壓力上色；2026-10-07 取代原本的風量環）
	sectionRim: "#e2f3fb",
	legendBg: "rgba(7, 22, 36, 0.78)",
	legendText: "#e2f3fb",
	legendMuted: "#8fb8cc",
} as const;

// CFD 探針（點擊即時查詢，2026-10-07）：3D 標記與讀數卡。卡片沿用流場讀值的深藍底；
// 標記外圈白、內點用壓力發散色階（flowTrailColors.pressureStops），方向箭頭青色；「模擬示意」標章琥珀色。
export const cfdProbeColors = {
	markerRing: "#ffffff",
	arrow: "#58edff",
	cardBg: "rgba(7, 22, 36, 0.9)",
	cardBorder: "rgba(88, 237, 255, 0.55)",
	cardText: "#e2f3fb",
	cardMuted: "#8fb8cc",
	cardTitle: "#58edff",
	badgeBg: "rgba(251, 191, 36, 0.14)",
	badgeBorder: "rgba(251, 191, 36, 0.5)",
	badgeText: "#fbbf24",
	estimateText: "#fbbf24",
	closeHover: "rgba(255, 255, 255, 0.12)",
	hintBg: "rgba(7, 22, 36, 0.92)",
	hintText: "#ffd36a",
	// 收合成一行的舊探針（超過 3 支時只有最新 3 張展開）：底色加深避免後方卡片文字透出
	pillBg: "rgba(7, 22, 36, 0.96)",
} as const;

// 聲波粒子（SoundParticles）的頻率色階：低頻深藍紫 → 中頻綠 → 高頻黃白，五個等距色票（0、.25、.5、.75、1）。
// 在淺色天空底上仍要看得見，所以低頻端用偏深的藍紫、高頻端的黃白略帶暖黃而非純白。
export const acousticParticleColors = {
	stops: ["#5b3fd6", "#3f7fe8", "#2fd08a", "#d4ee52", "#fff3b0"],
} as const;

// 單台風機葉輪流場（ImpellerFlow，2026-10-07）：右側窄軌按鈕與讀值卡。
// 與 CFD 窄軌同一套深藍底＋青色啟用態；「模擬」標章用琥珀色，提醒數字是示意非量測。
export const impellerFlowColors = {
	railBg: "rgba(7, 22, 36, 0.92)",
	railBorder: "rgba(99, 202, 235, 0.32)",
	btnBg: "#182b39",
	btnBorder: "#597486",
	btnActiveBg: "#087d98",
	btnActiveBorder: "#58edff",
	btnText: "#effbff",
	divider: "rgba(99, 202, 235, 0.22)",
	text: "#d9f0ff",
	textMuted: "#8fb8cc",
	accent: "#58edff",
	mockBadgeBg: "rgba(251, 191, 36, 0.14)",
	mockBadgeBorder: "rgba(251, 191, 36, 0.5)",
	mockBadgeText: "#fbbf24",
	good: "#4ade80",
	warn: "#fbbf24",
	error: "#f87171",
	legendBorder: "rgba(197, 213, 221, 0.8)",
} as const;

// 屋頂第一人稱漫步（components/walk，2026-10-07）：HUD 噪音計、小地圖、觸控搖桿。
// 與聲學面板同一套深色半透明底；讀數顏色依「與管制值的差」：超標紅、3 dB 內琥珀、以下青綠。
export const walkColors = {
	// 漫步 HUD 圖層開關（CFD 軌跡／聲音雲）開啟態
	layerOnBg: "rgba(20, 150, 170, 0.85)",
	layerOnBorder: "#58edff",
	panelBg: "rgba(10, 14, 22, 0.78)",
	panelBorder: "rgba(255,255,255,0.16)",
	text: "#ffffff",
	textMuted: "rgba(255,255,255,0.68)",
	textFaint: "rgba(255,255,255,0.45)",
	over: "#f87171",
	near: "#fbbf24",
	ok: "#2dd4bf",
	badgeBg: "rgba(251, 191, 36, 0.14)",
	badgeBorder: "rgba(251, 191, 36, 0.5)",
	badgeText: "#fbbf24",
	realText: "#4ade80",
	realBorder: "rgba(34,197,94,0.6)",
	tagBg: "rgba(10, 14, 22, 0.72)",
	tagBorder: "rgba(255,255,255,0.25)",
	crosshair: "rgba(255,255,255,0.85)",
	mapRoof: "rgba(148, 163, 184, 0.22)",
	mapEdge: "rgba(255,255,255,0.35)",
	mapBuilding: "rgba(148, 163, 184, 0.75)",
	mapPipe: "#60a5fa",
	mapFan: "#f59e0b",
	mapFanReal: "#4ade80",
	mapSelf: "#ffffff",
	mapCone: "rgba(255,255,255,0.22)",
	stickBase: "rgba(10, 14, 22, 0.45)",
	stickKnob: "rgba(255,255,255,0.55)",
	// 右下入口鈕（與聲學地圖鈕同一造型，換成步行的藍紫漸層）
	btnFrom: "#3b82f6",
	btnTo: "#8b5cf6",
	btnShadow: "rgba(99,102,241,0.38)",
} as const;

// 漫步巡檢（components/walk/inspection，2026-10-13）：清單狀態燈、目標指引、信標、設備卡、小地圖台標
export const walkInspectionColors = {
	abnormal: "#ef4444",
	attention: "#f59e0b",
	ok: "#22c55e",
	stopped: "rgba(255,255,255,0.35)",
	inspected: "#60a5fa",
	inspectedBg: "rgba(96,165,250,0.14)",
	target: "#58edff",
	targetBg: "rgba(88,237,255,0.12)",
	rowHover: "rgba(255,255,255,0.06)",
	rowBorder: "rgba(255,255,255,0.08)",
	tabOnBg: "rgba(255,255,255,0.12)",
	chipBg: "rgba(255,255,255,0.08)",
	ringTrack: "rgba(255,255,255,0.18)",
	btnBg: "rgba(255,255,255,0.08)",
	btnBorder: "rgba(255,255,255,0.22)",
	workOrder: "#4dabf7",
	workOrderBg: "rgba(77,171,247,0.14)",
	workOrderBorder: "rgba(77,171,247,0.45)",
	mapBadgeText: "#000000",
	unsynced: "#fbbf24",
} as const;

// 漫步第三人稱的工程師角色（components/walk/WalkEngineer，2026-10-07）：安全帽、反光背心、工作褲、安全鞋。
export const walkCharacterColors = {
	helmet: "#facc15",
	helmetBrim: "#eab308",
	skin: "#e0b48f",
	hair: "#2b2118",
	vest: "#c6f432",
	vestEmissive: "#5a7a00",
	reflective: "#e5e7eb",
	reflectiveEmissive: "#9ca3af",
	shirt: "#1e3a5f",
	pants: "#1e2a44",
	shoe: "#1f1f1f",
	shoeToe: "#f59e0b",
	glove: "#d4d4d4",
} as const;

// 漫步時嵌在電箱門上的 VFD 面板（shopfloor/VFDPanel mount 模式，2026-10-07）
export const walkCabinetPanelColors = {
	bg: "rgba(14, 18, 26, 0.97)",
	frame: "#5b6472",
} as const;

// 浮空 VFD 面板的「監測面板」鈕（一般模式，2026-10-13）：沿用面板標題的藍
export const vfdPanelMonitorColors = {
	text: "#4dabf7",
	border: "rgba(77, 171, 247, 0.55)",
	bg: "rgba(77, 171, 247, 0.10)",
	bgHover: "rgba(77, 171, 247, 0.24)",
} as const;

// 電箱門上 VFD 螢幕貼圖（3D 網格，參與深度遮擋；走近才換成可操作的 DOM 面板，2026-10-07）
export const walkVfdScreenColors = {
	bg: "#0e121a",
	frame: "#5b6472",
	title: "#4dabf7",
	run: "#22c55e",
	stop: "#ff4444",
	hz: "#4dabf7",
	unit: "#888888",
	label: "rgba(255,255,255,0.45)",
	readoutBg: "rgba(0,0,0,0.35)",
	track: "rgba(255,255,255,0.12)",
	hint: "rgba(255,255,255,0.55)",
} as const;

// 64 台壓力測試：風量自動平衡面板（components/stress/StressBalancePanel，2026-10-13）
export const stressBalanceColors = {
	panelBg: "rgba(7, 22, 36, 0.86)",
	panelBorder: "rgba(88, 237, 255, 0.28)",
	text: "#dff8ff",
	muted: "#8fb8cc",
	accent: "#58edff",
	bar: "#3fb6d6",
	/** 已到 60 Hz 仍低於平均 */
	barMax: "#ff7a59",
	/** 已到下限仍高於平均 */
	barMin: "#ffd36a",
	/** 開啟平衡當下的風量刻度 */
	beforeTick: "rgba(255, 255, 255, 0.55)",
	meanLine: "rgba(255, 255, 255, 0.45)",
	ok: "#7ee0a1",
	warn: "#ffb36a",
} as const;

// 虛擬廠房頂欄「風機總覽」（components/fanlist/FleetOverview，2026-10-13，> 12 台時取代一台一卡的頂欄）。
// 健康四區與 ControlPanel zoneLedColor 同值（ISO 10816-3 A/B/C/D），讓 7 台頂欄與 64 台總覽同一台機器同一個顏色；
// 底色、按鈕色沿用 ControlPanel 頂欄卡片與「全部啟動／停止」按鈕。
export const fleetOverviewColors = {
	zoneA: "#22c55e",
	zoneB: "#facc15",
	zoneC: "#f97316",
	zoneD: "#ef4444",
	stopped: "rgba(255, 255, 255, 0.28)",
	offline: "rgba(255, 255, 255, 0.16)",
	barBg: "rgba(20, 28, 40, 0.88)",
	barBorder: "rgba(255, 255, 255, 0.14)",
	panelBg: "rgba(12, 18, 28, 0.82)",
	panelBorder: "rgba(255, 255, 255, 0.16)",
	cellBg: "rgba(255, 255, 255, 0.04)",
	groupBg: "rgba(255, 255, 255, 0.03)",
	text: "#ffffff",
	muted: "rgba(255, 255, 255, 0.6)",
	faint: "rgba(255, 255, 255, 0.35)",
	accent: "#4dabf7",
	accentBg: "rgba(77, 171, 247, 0.15)",
	accentBorder: "rgba(77, 171, 247, 0.5)",
	start: "#00cc66",
	startBg: "rgba(0, 204, 102, 0.1)",
	startBorder: "rgba(0, 204, 102, 0.35)",
	stop: "#ff4444",
	stopBg: "rgba(255, 68, 68, 0.1)",
	stopBorder: "rgba(255, 68, 68, 0.35)",
	ghostBorder: "rgba(255, 255, 255, 0.1)",
	ghostText: "rgba(255, 255, 255, 0.4)",
} as const;
